import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Save, RefreshCw, Loader2, Check, AlertCircle,
  Instagram, Facebook, Eye, EyeOff, Hash, Users, Plus, Trash2,
  Youtube, Search, BarChart3, Clock, X, ExternalLink, FolderOpen
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function BrandSettingsCard({ brand, onUpdate }) {
  const [voice, setVoice] = useState(brand.voice_profile || "");
  const [saving, setSaving] = useState(false);
  // Reel edit settings live in brand_config (not the brand doc)
  const [clipFormat, setClipFormat] = useState("letterbox");
  const [clipSeconds, setClipSeconds] = useState(60);
  const [letterboxBg, setLetterboxBg] = useState("blur");
  const [rotateHint, setRotateHint] = useState(true);
  const [rotateDir, setRotateDir] = useState("cw");
  const [hookOpen, setHookOpen] = useState(true);
  const [burnSubtitles, setBurnSubtitles] = useState(true);
  const [titleCard, setTitleCard] = useState(true);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  useEffect(() => {
    api.get(`/config/${brand.id}`).then((r) => {
      setClipFormat(r.data?.clip_format || "letterbox");
      setClipSeconds(r.data?.target_clip_duration || 60);
      setLetterboxBg(r.data?.letterbox_background || "blur");
      setRotateHint(r.data?.rotate_hint !== false);
      setRotateDir(r.data?.rotate_direction || "cw");
      setHookOpen(r.data?.hook_open !== false);
      setBurnSubtitles(r.data?.burn_subtitles !== false);
      setTitleCard(r.data?.title_card !== false);
      setCfgLoaded(true);
    }).catch(() => setCfgLoaded(true));
  }, [brand.id]);

  const handleSaveClipSettings = async () => {
    setSavingCfg(true);
    try {
      await api.put(`/config/${brand.id}`, {
        clip_format: clipFormat,
        target_clip_duration: Math.max(15, Math.min(180, Number(clipSeconds) || 60)),
        letterbox_background: letterboxBg,
        rotate_hint: rotateHint,
        rotate_direction: rotateDir,
        hook_open: hookOpen,
        burn_subtitles: burnSubtitles,
        title_card: titleCard,
      });
      toast.success(`${brand.short_name}: reel format saved — applies to newly processed videos`);
    } catch { toast.error("Failed to save reel format"); }
    setSavingCfg(false);
  };

  const reelToggles = [
    { key: "hook", label: "Hook cold-open", hint: "Reel opens on the film's peak 2-4s moment", value: hookOpen, set: setHookOpen },
    { key: "subs", label: "Speech subtitles", hint: "Burn timed captions when the segment has vows/toasts", value: burnSubtitles, set: setBurnSubtitles },
    { key: "title", label: "Title card", hint: "Couple names + venue in the letterbox area", value: titleCard, set: setTitleCard },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/brands/${brand.id}`, { voice_profile: voice });
      toast.success(`${brand.name} updated`);
      if (onUpdate) onUpdate();
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5 brand-card" data-testid={`settings-brand-${brand.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-3 h-3 rounded-full" style={{ background: brand.color_hex }} />
        <h3 className="font-sans font-semibold text-zinc-100 text-sm">{brand.name}</h3>
        <span className="text-xs text-zinc-500 font-mono ml-auto">{brand.short_name}</span>
      </div>

      <div className="space-y-3 text-xs">
        <div className="flex items-center gap-2 text-zinc-400">
          <Facebook size={14} className="platform-fb" />
          <span>FB Page:</span>
          <span className="font-mono text-zinc-300">{brand.fb_page_id}</span>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <Instagram size={14} className="platform-ig" />
          <span>IG ID:</span>
          <span className="font-mono text-zinc-300">{brand.ig_id}</span>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <span className="w-2 h-2 rounded-full bg-status-green" />
          <span>Vimeo:</span>
          <span className="font-mono text-zinc-300">{brand.vimeo_token ? "Connected" : "Not connected"}</span>
        </div>
        <div className="flex items-center gap-2 text-zinc-400">
          <span>Type:</span>
          <span className="text-zinc-300">{brand.type}</span>
          {brand.location && <><span className="text-zinc-600">--</span><span className="text-zinc-300">{brand.location}</span></>}
        </div>
      </div>

      <div className="mt-4">
        <label className="section-label block mb-2">// brand voice</label>
        <textarea
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          className="w-full h-20 bg-studio-surface border border-[#2a2e36] rounded-xl p-3 text-sm text-zinc-200 outline-none focus:border-gold-dim resize-none placeholder:text-zinc-600"
          data-testid={`voice-editor-${brand.id}`}
        />
        <button
          onClick={handleSave}
          disabled={saving || voice === brand.voice_profile}
          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-gold text-xs disabled:opacity-40"
          data-testid={`save-voice-${brand.id}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save Voice
        </button>
      </div>

      {/* Reel edit format — how the AI cut is framed for the 9:16 Reel canvas */}
      <div className="mt-5 pt-4 border-t border-[#222630]">
        <label className="section-label block mb-2">// reel edit</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-[10px] text-zinc-500 mb-1">Framing</p>
            <Select value={clipFormat} onValueChange={setClipFormat} disabled={!cfgLoaded}>
              <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-xs h-9 rounded-lg" data-testid={`clip-format-${brand.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-studio-raised border-[#2a2e36]">
                <SelectItem value="letterbox" className="text-zinc-300 text-xs">Letterbox — full horizontal frame, black bars</SelectItem>
                <SelectItem value="rotate" className="text-zinc-300 text-xs">Rotate — full screen sideways, viewer turns phone</SelectItem>
                <SelectItem value="smart_crop" className="text-zinc-300 text-xs">Smart crop — face-tracked 9:16</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[10px] text-zinc-500 mb-1">Target length (sec)</p>
            <input type="number" min={15} max={180} value={clipSeconds}
              onChange={(e) => setClipSeconds(e.target.value)}
              className="w-full h-9 bg-studio-surface border border-[#2a2e36] rounded-lg px-3 text-xs text-zinc-200 outline-none focus:border-gold-dim"
              data-testid={`clip-seconds-${brand.id}`} />
          </div>
        </div>
        {clipFormat === "rotate" && (
          <div className="mt-3 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer" data-testid={`rotate-hint-${brand.id}`}>
              <input type="checkbox" checked={rotateHint} onChange={(e) => setRotateHint(e.target.checked)}
                disabled={!cfgLoaded} className="mt-0.5 accent-[#c6a96f]" />
              <span className="text-xs text-zinc-300 leading-tight">
                Rotate hint
                <span className="block text-[10px] text-zinc-600">Animated "rotate your phone" prompt over the first few seconds</span>
              </span>
            </label>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Turn direction</p>
              <Select value={rotateDir} onValueChange={setRotateDir} disabled={!cfgLoaded}>
                <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-xs h-9 rounded-lg" data-testid={`rotate-dir-${brand.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-studio-raised border-[#2a2e36]">
                  <SelectItem value="cw" className="text-zinc-300 text-xs">Clockwise — turn phone right</SelectItem>
                  <SelectItem value="ccw" className="text-zinc-300 text-xs">Counter-clockwise — turn phone left</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {clipFormat === "letterbox" && (
          <div className="mt-2">
            <p className="text-[10px] text-zinc-500 mb-1">Letterbox background</p>
            <Select value={letterboxBg} onValueChange={setLetterboxBg} disabled={!cfgLoaded}>
              <SelectTrigger className="bg-studio-surface border-[#2a2e36] text-zinc-300 text-xs h-9 rounded-lg" data-testid={`letterbox-bg-${brand.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-studio-raised border-[#2a2e36]">
                <SelectItem value="blur" className="text-zinc-300 text-xs">Blurred fill — frame echoed behind, cinematic</SelectItem>
                <SelectItem value="black" className="text-zinc-300 text-xs">Black bars — classic letterbox</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {clipFormat === "letterbox" && (
          <div className="mt-3 space-y-2">
            {reelToggles.map((t) => (
              <label key={t.key} className="flex items-start gap-2 cursor-pointer group" data-testid={`reel-${t.key}-${brand.id}`}>
                <input type="checkbox" checked={t.value} onChange={(e) => t.set(e.target.checked)}
                  disabled={!cfgLoaded} className="mt-0.5 accent-[#c6a96f]" />
                <span className="text-xs text-zinc-300 leading-tight">
                  {t.label}
                  <span className="block text-[10px] text-zinc-600">{t.hint}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
          Letterbox keeps the whole shot (wide shots, venues, dance floors survive). Smart crop fills the phone screen but cuts off the sides.
          Applies to videos processed after saving; reprocess existing ones to re-render.
        </p>
        <button onClick={handleSaveClipSettings} disabled={savingCfg || !cfgLoaded}
          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-gold text-xs disabled:opacity-40"
          data-testid={`save-clip-${brand.id}`}>
          {savingCfg ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save Reel Format
        </button>
      </div>
    </div>
  );
}

function HashtagSection() {
  const [sets, setSets] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const [hsRes, bRes] = await Promise.all([api.get("/hashtags"), api.get("/brands")]);
        setSets(hsRes.data.sets);
        setBrands(bRes.data.brands);
      } catch { toast.error("Failed to load hashtags"); }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleSave = async (setId) => {
    try {
      const hashtags = editValue.split(",").map((h) => h.trim()).filter(Boolean);
      await api.put(`/hashtags/${setId}`, { hashtags });
      toast.success("Hashtag set updated");
      setEditingId(null);
      const res = await api.get("/hashtags");
      setSets(res.data.sets);
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="spinner-gold" /></div>;

  const brandMap = {};
  brands.forEach((b) => { brandMap[b.id] = b; });

  const grouped = {};
  sets.forEach((s) => {
    if (!grouped[s.brand_id]) grouped[s.brand_id] = [];
    grouped[s.brand_id].push(s);
  });

  return (
    <div className="space-y-6" data-testid="hashtag-settings">
      {Object.entries(grouped).map(([brandId, brandSets]) => {
        const brand = brandMap[brandId];
        return (
          <div key={brandId} className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: brand?.color_hex || "#71717a" }} />
              <span className="text-sm font-semibold text-zinc-200">{brand?.name || brandId}</span>
            </div>
            <div className="space-y-3">
              {brandSets.sort((a, b) => a.set_name.localeCompare(b.set_name)).map((s) => (
                <div key={s.id} className="p-3 bg-studio-surface rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="section-label">Set {s.set_name}</span>
                    <button
                      onClick={() => {
                        if (editingId === s.id) { handleSave(s.id); }
                        else { setEditingId(s.id); setEditValue(s.hashtags.join(", ")); }
                      }}
                      className="text-xs text-zinc-400 hover:text-gold transition-colors"
                      data-testid={`edit-hashtag-${s.id}`}
                    >
                      {editingId === s.id ? "Save" : "Edit"}
                    </button>
                  </div>
                  {editingId === s.id ? (
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full h-16 bg-studio-deepest border border-[#2a2e36] rounded-lg p-2 text-xs text-zinc-300 outline-none focus:border-gold-dim resize-none"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {s.hashtags.map((tag) => (
                        <span key={tag} className="text-[10px] text-teal font-mono bg-teal/5 px-1.5 py-0.5 rounded">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConnectionsSection() {
  const [dropboxStatus, setDropboxStatus] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [metaConnecting, setMetaConnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const [dbRes, metaRes] = await Promise.all([
          api.get("/dropbox/status").catch(() => ({ data: null })),
          api.get("/meta/auth/status").catch(() => ({ data: null })),
        ]);
        setDropboxStatus(dbRes.data);
        setMetaStatus(metaRes.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchStatuses();
  }, []);

  const handleConnectDropbox = async () => {
    setConnecting(true);
    try {
      const res = await api.get("/dropbox/connect");
      if (res.data.auth_url) {
        window.open(res.data.auth_url, "_blank");
      } else {
        toast.error("Could not generate Dropbox auth URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect");
    }
    setConnecting(false);
  };

  const handleConnectMeta = async () => {
    setMetaConnecting(true);
    try {
      const res = await api.get("/meta/auth/url");
      if (res.data.auth_url) {
        window.open(res.data.auth_url, "_blank");
      } else {
        toast.error("Could not generate Meta auth URL");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect");
    }
    setMetaConnecting(false);
  };

  const handleDisconnectMeta = async () => {
    try {
      await api.post("/meta/auth/disconnect");
      setMetaStatus({ connected: false });
      toast.success("Meta disconnected");
    } catch { toast.error("Failed to disconnect"); }
  };

  const connections = [
    { name: "Meta Graph API v19.0", status: "connected", desc: "Publishing to Instagram & Facebook for all brands" },
    { name: "Vimeo API", status: "connected", desc: "4 brand tokens — S&S, BL, LIC, ROYWD" },
    { name: "YouTube Data API v3", status: "connected", desc: "S&S channel monitoring" },
    { name: "Google Gemini 2.5 Flash", status: "connected", desc: "Jarvis AI brain — captions, strategy, analysis" },
    { name: "Kling AI v1.6", status: "configured", desc: "Video generation — needs account credits" },
    { name: "Meta Ads API", status: "connected", desc: "ROYWD ad account" },
  ];

  if (loading) return <div className="flex justify-center py-8"><div className="spinner-gold" /></div>;

  return (
    <div className="space-y-4" data-testid="connections-settings">
      <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">API Connections</h3>
        <div className="space-y-3">
          {connections.map((conn) => (
            <div key={conn.name} className="flex items-center justify-between p-3 bg-studio-surface rounded-lg">
              <div>
                <p className="text-sm text-zinc-200">{conn.name}</p>
                <p className="text-xs text-zinc-500">{conn.desc}</p>
              </div>
              <span className={`status-badge ${conn.status === "connected" ? "status-published" : "status-scheduled"}`}>
                {conn.status}
              </span>
            </div>
          ))}

          {/* Dropbox — special handling with OAuth connect */}
          <div className="flex items-center justify-between p-3 bg-studio-surface rounded-lg">
            <div>
              <p className="text-sm text-zinc-200">Dropbox</p>
              <p className="text-xs text-zinc-500">
                {dropboxStatus?.connected
                  ? dropboxStatus.source === "env_token"
                    ? "Connected via short-lived token (will expire)"
                    : `Connected — account ${dropboxStatus.account_id || ""} — auto-refreshing`
                  : "Not connected — click to authorize"}
              </p>
            </div>
            {dropboxStatus?.connected && dropboxStatus.source !== "env_token" ? (
              <span className="status-badge status-published">connected</span>
            ) : (
              <button onClick={handleConnectDropbox} disabled={connecting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-gold text-xs disabled:opacity-40"
                data-testid="connect-dropbox-btn">
                {connecting ? <Loader2 size={12} className="animate-spin" /> : <FolderOpen size={12} />}
                Connect Dropbox
              </button>
            )}
          </div>

          {/* Meta — OAuth connect for App Review */}
          <div className="flex items-center justify-between p-3 bg-studio-surface rounded-lg border border-[#2a2e36]">
            <div>
              <p className="text-sm text-zinc-200">Meta (Facebook + Instagram)</p>
              <p className="text-xs text-zinc-500">
                {metaStatus?.connected && metaStatus.source !== "env_token"
                  ? `Connected as ${metaStatus.user_name || "Unknown"}`
                  : "Connect your Facebook account to manage pages and ads"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {metaStatus?.connected && metaStatus.source !== "env_token" && (
                <>
                  <span className="status-badge status-published">connected</span>
                  <button onClick={handleDisconnectMeta}
                    className="text-xs text-zinc-600 hover:text-red-400 transition-colors"
                    data-testid="disconnect-meta-btn">
                    Disconnect
                  </button>
                </>
              )}
              {(!metaStatus?.connected || metaStatus?.source === "env_token") && (
                <button onClick={handleConnectMeta} disabled={metaConnecting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-gold text-xs disabled:opacity-40"
                  data-testid="connect-meta-btn">
                  {metaConnecting ? <Loader2 size={12} className="animate-spin" /> : <Facebook size={12} />}
                  Connect to Meta
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-2">Processing Settings</h3>
        <p className="section-label mb-4">// video processing</p>
        <div className="space-y-3 text-sm text-zinc-400">
          <div className="flex justify-between"><span>Target clip duration</span><span className="font-mono text-zinc-200">30-45s</span></div>
          <div className="flex justify-between"><span>Face tracking</span><span className="font-mono text-zinc-200">OpenCV cascade, 0.25x scale, 3s smoothing</span></div>
          <div className="flex justify-between"><span>Output format</span><span className="font-mono text-zinc-200">1080x1920 (9:16 vertical)</span></div>
          <div className="flex justify-between"><span>Codec</span><span className="font-mono text-zinc-200">H.264 + AAC 128k</span></div>
          <div className="flex justify-between"><span>Auto-delete temp files</span><span className="font-mono text-status-green">enabled</span></div>
          <div className="flex justify-between text-xs text-zinc-600 pt-2 border-t border-[#222630]">
            <span>Note: "auto-delete" only removes the downloaded source file from the server after processing.</span>
          </div>
          <div className="flex justify-between text-xs text-zinc-600">
            <span>It NEVER deletes anything from Vimeo, YouTube, or Dropbox. Your originals are always safe.</span>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function Settings() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBrands = async () => {
    try {
      const res = await api.get("/brands");
      setBrands(res.data.brands);
    } catch { toast.error("Failed to load settings"); }
    setLoading(false);
  };

  useEffect(() => { fetchBrands(); }, []);

  if (loading) return <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Settings</h1>
        <p className="section-label mt-2">// configuration</p>
      </div>

      <Tabs defaultValue="brands">
        <TabsList className="bg-studio-surface border border-[#2a2e36] rounded-xl p-1 h-auto">
          <TabsTrigger value="brands" className="text-xs data-[state=active]:bg-gold/10 data-[state=active]:text-gold rounded-lg px-4 py-2" data-testid="settings-tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="hashtags" className="text-xs data-[state=active]:bg-gold/10 data-[state=active]:text-gold rounded-lg px-4 py-2" data-testid="settings-tab-hashtags">Hashtags</TabsTrigger>
          <TabsTrigger value="connections" className="text-xs data-[state=active]:bg-gold/10 data-[state=active]:text-gold rounded-lg px-4 py-2" data-testid="settings-tab-connections">Connections</TabsTrigger>
        </TabsList>

        <TabsContent value="brands" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {brands.map((b) => (
              <BrandSettingsCard key={b.id} brand={b} onUpdate={fetchBrands} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="hashtags" className="mt-6">
          <HashtagSection />
        </TabsContent>

        <TabsContent value="connections" className="mt-6">
          <ConnectionsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
